import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SettingsIcon from "@mui/icons-material/Settings";
import TableViewIcon from "@mui/icons-material/TableView";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCallout } from "@/components/help/HelpCallout";
import { HelpDrawer } from "@/components/help/HelpDrawer";
import { HelpSection } from "@/components/help/HelpSection";

const STEP_KEYS = ["period", "vehicles", "reason", "review"] as const;

const COLUMN_KEYS = [
  "device",
  "licensePlate",
  "vin",
  "distance",
  "start",
  "startAddress",
  "stop",
  "stopAddress",
  "reason",
] as const;

const TIP_KEYS = ["limit", "addresses", "persistence"] as const;

export default function LogbookCheckHelp() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Tooltip title={t("logbookCheck.help.open")}>
        <IconButton onClick={() => setOpen(true)} aria-label={t("logbookCheck.help.open")}>
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
      <HelpDrawer
        open={open}
        onClose={() => setOpen(false)}
        eyebrow={t("logbookCheck.help.eyebrow")}
        title={t("logbookCheck.help.title")}
        closeLabel={t("logbookCheck.help.close")}
      >
            <HelpSection icon={<InfoOutlinedIcon fontSize="small" />} title={t("logbookCheck.help.purpose.title")}>
              <Typography variant="body2" color="text.secondary">
                {t("logbookCheck.help.purpose.body")}
              </Typography>
            </HelpSection>

            <HelpSection icon={<PlaylistAddCheckIcon fontSize="small" />} title={t("logbookCheck.help.steps.title")}>
              <Stack spacing={2}>
                {STEP_KEYS.map((key, index) => (
                  <Stack key={key} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        mt: 0.25,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "common.white",
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t(`logbookCheck.help.steps.${key}.title`)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(`logbookCheck.help.steps.${key}.body`)}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </HelpSection>

            <HelpSection icon={<ViewColumnIcon fontSize="small" />} title={t("logbookCheck.help.columns.title")}>
              <Stack spacing={1.25}>
                {COLUMN_KEYS.map(key => (
                  <Box key={key}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(key)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(`logbookCheck.help.columns.${key}`)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <HelpCallout>{t("logbookCheck.help.columns.hiddenHint")}</HelpCallout>
            </HelpSection>

            <HelpSection icon={<SettingsIcon fontSize="small" />} title={t("logbookCheck.help.settings.title")}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("logbookCheck.showArchived")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("logbookCheck.help.settings.archived")}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("distance")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("logbookCheck.help.settings.decimals")}
                  </Typography>
                </Box>
              </Stack>
            </HelpSection>

            <HelpSection icon={<TableViewIcon fontSize="small" />} title={t("logbookCheck.help.export.title")}>
              <Typography variant="body2" color="text.secondary">
                {t("logbookCheck.help.export.body")}
              </Typography>
            </HelpSection>

            <HelpSection icon={<LightbulbOutlinedIcon fontSize="small" />} title={t("logbookCheck.help.tips.title")}>
              <Stack spacing={1.25}>
                {TIP_KEYS.map(key => (
                  <Stack key={key} direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        flexShrink: 0,
                        width: 6,
                        height: 6,
                        mt: 0.9,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t(`logbookCheck.help.tips.${key}`)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </HelpSection>
      </HelpDrawer>
    </>
  );
}
